import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import beSetupStripeSubscription from 'actions/billing/beSetupStripeSubscription.action';
import Button from 'components/form/Button.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import CONF from 'config/conf.singleton';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';

import CN from './SetupForm.module.scss';

// NOTE: Can't be read in react dev tools, check in future
const SetupForm = ({ beSetupStripeSubscription, handleGoBack, salonId, onSuccess, createSubscription = true }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState(null);
  const { val: isLoading, yay: showLoading, nay: hideLoading } = useTip(false);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements || isLoading) return;

    showLoading();
    setErrorMessage(null);

    try {
      const response = await stripe.confirmSetup({
        // `Elements` instance that was used to create the Payment Element
        elements,
        confirmParams: {
          return_url: `${CONF.salonUrl}${salonId}/settings/billing`,
        },
        redirect: 'if_required',
      });
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      if (response.error) {
        setErrorMessage(getAnyOrVoid('error.message', response));
        return;
      }

      const id = getAnyOrVoid('setupIntent.id', response);

      if (!id) {
        setErrorMessage(t('common.errors.wrongResponse'));
        return;
      }

      if (createSubscription) {
        const result = await beSetupStripeSubscription({
          salonId,
          intentId: id,
        });

        if (!result?.is?.ok) {
          setErrorMessage(t('staffAndBusiness.subAndBilling.billingDetails.cardFormErrorMsg'));
          return;
        }
      }

      await onSuccess?.();
    } finally {
      hideLoading();
    }
  }, [beSetupStripeSubscription, createSubscription, elements, hideLoading, isLoading, onSuccess, salonId, showLoading, stripe, t]);

  return (
    <form
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <PaymentElement />
      <div className={CN.buttonWrap}>
        <Button variant="outlineForce" onClick={handleGoBack} xSpace>
          {t('common.buttons.cancelUpp')}
        </Button>
        <Button tag="SetupForm__save" variant="primaryForce" onClick={handleSubmit} disabled={!stripe || !elements || isLoading} xSpace>
          {t('common.buttons.saveChanges')}
        </Button>
      </div>
      {isLoading && <OverlaySpinner />}
      {/* Show error message to your customers */}
      {errorMessage && <div className={CN.error}>{errorMessage} </div>}
    </form>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      beSetupStripeSubscription,
    },
    dispatch,
  );

export default connect(null, mapDispatchToProps)(SetupForm);
